import React, { memo, useCallback, useEffect } from 'react';
import cx from 'classnames';
import ReactTooltip from 'react-tooltip';
import { Link, Tooltip } from '@ontruck/orc';
import { NavLink } from 'react-router-dom';
import { DISPATCHING } from 'modules/fleet/events';

import { events as routingEvents, RoutingEventEmitter } from 'modules/routing';
import AssignmentMapEventEmitter, { events } from 'modules/assignment-map/events';
import { useDispatchingUI } from 'modules/dispatching/ui';

import {
  CommentsCell,
  DistanceCell,
  ExtrasCell,
  HeightCell,
  PalletsCell,
  RouteType,
  WeightCell,
} from 'utils/format';
import { StopCell } from 'modules/fleet/list';

import StopsTooltip from 'modules/dispatching/stops-tooltip';
import { DispatchingShipperCell, ShippersTooltip } from 'modules/fleet/common';
import ActionsTooltip from 'modules/dispatching/actions-tooltip';
import LogsTooltip from 'modules/dispatching/logs-tooltip';

import { ReactComponent as LogIcon } from 'assets/icons/log.svg';

import ItineraryCostCell from 'modules/dispatching/cost-cell';
import ItineraryBiddingCell from 'modules/dispatching/bidding-cell';

import {
  cellWidth,
  isAssignedToAgent,
  isExtraPresent,
  isLocked,
  isUpgrade,
} from 'utils/itineraries';

import ListIndicator from 'modules/fleet/list-indicator';
import { HighPrioTag } from '../../high-prio-order';
import InitialsIcon from '../../../components/InitialsIcon';
import { injectIntl } from 'react-intl';

import { IS_TENANT } from 'tenant/config';
import { HOST, TENANTS_NAMES } from 'tenant/host_config';
import { getTenantPreLoginConfig } from 'tenant/teanantPreLoginConfig';

const Ghost = (props) => <div {...props} />;
const noop = () => {};

const DispatchingListItem = ({ className, loading, to, itinerary, suggestionFound, intl }) => {
  const t = intl.messages.modules.dispatching.list_item;
  const { setLoading, mutateListFn } = useDispatchingUI();

  const { id } = itinerary || {};

  const handleRouteFoundClick = useCallback(() => {
    DISPATCHING.ROUTE_SUGGESTION_CLICK({
      itinerary_id: id,
      suggestionFound: suggestionFound?.id,
    });

    RoutingEventEmitter.emit(routingEvents.ADD_TESSA_ITINERARY, {
      itineraryId: suggestionFound?.id,
      baseItineraryEndpoint: 'itineraries',
    });
  }, [id, suggestionFound?.id]);

  const handleDistanceCell = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();

      DISPATCHING.DISTANCE_CLICK({
        itinerary_id: id,
      });

      AssignmentMapEventEmitter.emit(events.OPEN_MODAL, id);
    },
    [id]
  );

  const BaseComponent = itinerary && to ? NavLink : Ghost;

  const shouldShowSuggestions = !!suggestionFound && !isLocked(itinerary);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [id]);

  const hasHandling =
    itinerary?.stops?.some((stop) => stop?.extras?.indexOf('HANDLING_LOADING') >= 0) ||
    itinerary?.stops?.some((stop) => stop?.extras?.indexOf('HANDLING_UNLOADING') >= 0);

  const ignoredStops = getTenantPreLoginConfig().ignored_stops_during_operative;
  const numberOfStops = itinerary?.stops?.length - ignoredStops;

  const isIntraser = HOST === TENANTS_NAMES.intraser;
  const stops = itinerary?.stops ?? [];
  const middleStopsForIntraser = isIntraser && stops.length > 2 ? stops.slice(1, 2) : [];

  // For intraser, first/last stop label from stop; times from itinerary (same as other tenants)
  const firstStop = stops[0];
  const lastStop = stops.length > 0 ? stops[stops.length - 1] : null;
  const firstStopLabel =
    isIntraser && firstStop
      ? firstStop?.address?.company_name ?? firstStop?.address?.label ?? firstStop?.label ?? '—'
      : itinerary?.start_label;
  const lastStopLabel =
    isIntraser && lastStop
      ? lastStop?.address?.company_name ?? lastStop?.address?.label ?? lastStop?.label ?? '—'
      : itinerary?.end_label;
  const firstStopTimeFrom = itinerary?.start_time_from;
  const firstStopTimeTo = itinerary?.start_time_to;
  const lastStopTimeFrom = itinerary?.end_time_from;
  const lastStopTimeTo = itinerary?.end_time_to;
  const hasFirstStopTimes = firstStopTimeFrom && firstStopTimeTo;
  const hasLastStopTimes = lastStopTimeFrom && lastStopTimeTo;

  return (
    <BaseComponent
      to={to}
      className={cx(className, {
        [`${className}__loading`]: loading,
      })}
      activeClassName="selected"
      data-testid="dispatchingListItem"
    >
      <ListIndicator itinerary={itinerary} />
      <ShippersTooltip itinerary={itinerary}>
        <DispatchingShipperCell itinerary={itinerary} width="300px" />
      </ShippersTooltip>
      <div
        className={cx('cell cell__nowrap cell__nopadding cell__center', {
          pointer: shouldShowSuggestions,
        })}
        onClick={shouldShowSuggestions ? handleRouteFoundClick : noop}
        data-testid="dispatchingListItem__suggestion"
        {...cellWidth('42px')}
      >
        <RouteType
          isGroupage={itinerary?.is_groupage}
          isMultiroute={itinerary?.is_multiroute}
          suggestion={shouldShowSuggestions ? suggestionFound : false}
          upgrade={itinerary?.upgrade_journey}
          numberOfStops={numberOfStops}
        />
      </div>

      <StopsTooltip id={itinerary?.id}>
        <div className="tooltip-container pointer">
          {isIntraser && !hasFirstStopTimes ? (
            <div
              className="cell cell__stop"
              data-testid="dispatchingListItem__stop0"
              {...cellWidth('136px')}
            >
              <div className="stop__nowrap">{firstStopLabel}</div>
              <div className="stop__time">—</div>
            </div>
          ) : (
            <StopCell
              label={firstStopLabel}
              timeFrom={firstStopTimeFrom}
              timeTo={firstStopTimeTo}
              {...cellWidth('136px')}
            />
          )}
        </div>
      </StopsTooltip>

      {middleStopsForIntraser.map((stop, idx) => {
        const label =
          stop?.address?.company_name ??
          stop?.address?.label ??
          stop?.label ??
          (stop?.type ? `Stop ${stop.order + 1} (${stop.type})` : '—');
        const timeFrom = stop?.time_from;
        const timeTo = stop?.time_to;
        const hasTimes = timeFrom && timeTo;
        return (
          <StopsTooltip key={stop?.id ?? idx} id={itinerary?.id}>
            <div className="tooltip-container pointer">
              {!hasTimes ? (
                <div
                  className="cell cell__stop"
                  data-testid={`dispatchingListItem__stop${idx + 2}`}
                  {...cellWidth('136px')}
                >
                  <div className="stop__nowrap">{label}</div>
                  <div className="stop__time"></div>
                </div>
              ) : (
                <StopCell
                  label={label}
                  timeFrom={timeFrom}
                  timeTo={timeTo}
                  {...cellWidth('136px')}
                />
              )}
            </div>
          </StopsTooltip>
        );
      })}

      <div
        className="cell cell__center"
        {...cellWidth('64px')}
        data-testid="dispatchingListItem__distance"
      >
        <Link
          text={
            <DistanceCell
              distance={itinerary?.distance}
              distanceMiles={itinerary?.distance_miles}
              country={itinerary?.route?.country}
            />
          }
          onClick={handleDistanceCell}
        />
      </div>

      <StopsTooltip id={itinerary?.id}>
        <div className="tooltip-container pointer">
          {isIntraser && !hasLastStopTimes ? (
            <div
              className="cell cell__stop"
              data-testid="dispatchingListItem__stopLast"
              {...cellWidth('136px')}
            >
              <div className="stop__nowrap">{lastStopLabel}</div>
              <div className="stop__time">—</div>
            </div>
          ) : (
            <StopCell
              label={lastStopLabel}
              timeFrom={lastStopTimeFrom}
              timeTo={lastStopTimeTo}
              firstStopTimeFrom={itinerary?.start_time_from}
              {...cellWidth('136px')}
            />
          )}
          <div className="cell" {...cellWidth('36px')}>
            <CommentsCell hasComments={itinerary?.has_comments} />
          </div>
        </div>
      </StopsTooltip>

      <div className="cell cell__bold cell__right cell__dark" {...cellWidth('96px')}>
        <PalletsCell
          realPallets={itinerary?.real_pallets}
          equivalentPallets={itinerary?.equivalent_pallets}
          typePallets={itinerary?.type_pallets}
          loadTypes={itinerary?.load_types}
          country={itinerary?.route?.country}
          stops={itinerary?.stops}
        />
      </div>
      {HOST === TENANTS_NAMES.intraser ? (
        <>
          <div
            className="cell cell__right cell__dark"
            {...cellWidth('180px')}
            data-testid="dispatchingListItem__expedientOl"
            style={{
              width: '180px',
              maxWidth: '180px',
              minWidth: '180px',
              boxSizing: 'border-box',
              paddingRight: '20px',
            }}
          >
            {itinerary?.shipper_orders?.[0]?.logistics_operator_code ?? '—'}
          </div>
          <div
            className="cell cell__right cell__dark"
            {...cellWidth('100px')}
            data-testid="dispatchingListItem__bookingNumber"
            style={{
              width: '100px',
              maxWidth: '100px',
              minWidth: '100px',
              boxSizing: 'border-box',
            }}
          >
            {itinerary?.shipper_orders?.[0]?.booking_number ?? '—'}
          </div>
        </>
      ) : (
        <>
          <div className="cell cell__right cell__dark" {...cellWidth('72px')}>
            <WeightCell
              weight={IS_TENANT ? itinerary?.total_goods_weight : itinerary?.total_weight}
            />
          </div>
          <div className="cell cell__right cell__dark" {...cellWidth('56px')}>
            <HeightCell height={itinerary?.item_height} />
          </div>
        </>
      )}
      {HOST === TENANTS_NAMES.intraser ? null : (
        <div className="cell cell__flex cell__dark" {...cellWidth('114px')}>
          <ExtrasCell
            handling={hasHandling}
            lifgate={isExtraPresent('LIFTGATE', itinerary?.extras)}
            sideload={isExtraPresent('SIDE_LOAD', itinerary?.extras)}
            topload={isExtraPresent('TOP_LOAD', itinerary?.extras)}
          />
        </div>
      )}
      <div className="cell cell__flex cell__dark" {...cellWidth(isIntraser ? '34px' : '114px')}>
        {itinerary?.has_high_prio_shipper_order === true && <HighPrioTag />}
      </div>
      {!!itinerary && (
        <LogsTooltip itinerary={itinerary}>
          <div className="cell cell__center cell__dark cell__line pointer" {...cellWidth('36px')}>
            <span className="on_hover">
              <LogIcon />
            </span>
          </div>
        </LogsTooltip>
      )}
      <div className="cell cell__flex" {...cellWidth('44px')}>
        <ItineraryBiddingCell itinerary={itinerary} />
      </div>
      <ItineraryCostCell
        itinerary={itinerary}
        addLoading={(itineraryLoadingId) => {
          setLoading((currentLoading) => ({ ...currentLoading, [itineraryLoadingId]: true }));
        }}
        removeLoading={(itineraryLoadingId) => {
          setLoading((currentLoading) => ({ ...currentLoading, [itineraryLoadingId]: false }));
        }}
        mutateList={mutateListFn}
      />
      {isAssignedToAgent(itinerary) && (
        <div className="cell" {...cellWidth('36px')}>
          <Tooltip
            component={`${t.assigned_to}: ${itinerary.owner.name}`}
            position="top"
            skin="light"
          >
            <InitialsIcon initials={itinerary.owner.initials} fullName={itinerary.owner.name} />
          </Tooltip>
        </div>
      )}
      <div className="cell cell__grow"> &nbsp;</div>
      <div className="cell" {...cellWidth('76px')}>
        {!loading && (
          <ActionsTooltip
            id={itinerary?.id}
            journeyId={itinerary?.journey_id}
            shipperOrderIds={itinerary?.shipper_order_ids}
            pickupDate={itinerary?.start_time_from}
            route={itinerary?.route}
            isLocked={isLocked(itinerary)}
            isUpgrade={isUpgrade(itinerary)}
            isAssignedToAgent={isAssignedToAgent(itinerary)}
          >
            <button className={cx('actions_button')}>{t.actions}</button>
          </ActionsTooltip>
        )}
      </div>
    </BaseComponent>
  );
};

export default memo(injectIntl(DispatchingListItem));
